import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  subscription_status: string;
  subscription_plan: string | null;
  subscription_end_date: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, phone: string) => Promise<{ error: string | null }>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Failed to fetch profile:', error);
        setProfile(null);
        return;
      }

      if (data) setProfile(data);
    } catch (e) {
      console.error('Unexpected error while fetching profile:', e);
      setProfile(null);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  }, [user, fetchProfile]);

  useEffect(() => {
    // Initial session load
    supabase.auth
      .getSession()
      .then(({ data: { session: s } }) => {
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.user) fetchProfile(s.user.id);
        setLoading(false);
      })
      .catch((e) => {
        console.error('Error getting initial session:', e);
        setLoading(false);
      });

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        fetchProfile(s.user.id);
      } else {
        setProfile(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signUp = async (email: string, password: string, fullName: string, phone: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { 
          data: { 
            full_name: fullName, 
            phone: phone 
          } 
        },
      });
      
      if (error) {
        console.error('Sign up error:', error);
        return { error: error.message };
      }

      // Kullanıcı oluşturulduysa profili manuel olarak oluştur
      if (data?.user) {
        console.log('🔄 Kullanıcı oluşturuldu, profil kaydediliyor...', {
          userId: data.user.id,
          email: email,
          fullName: fullName,
          phone: phone
        });

        // Trigger'ın çalışması için kısa bir süre bekle
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Profil var mı kontrol et
        const { data: existingProfile, error: checkError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.user.id)
          .maybeSingle();

        if (checkError) {
          console.error('❌ Profil kontrol hatası:', checkError);
        }

        if (existingProfile) {
          console.log('✅ Profil zaten var, güncelleniyor...', existingProfile);
          
          // Profil varsa güncelle
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ 
              full_name: fullName,
              phone: phone,
              email: email
            })
            .eq('id', data.user.id);
          
          if (updateError) {
            console.error('❌ Profil güncelleme hatası:', updateError);
          } else {
            console.log('✅ Profil başarıyla güncellendi!');
          }
        } else {
          console.log('⚠️ Profil bulunamadı, yeni profil oluşturuluyor...');
          
          // Profil yoksa oluştur
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({ 
              id: data.user.id,
              email: email,
              full_name: fullName,
              phone: phone,
              subscription_status: 'free',
              subscription_plan: null,
              subscription_end_date: null,
              avatar_url: null
            });
          
          if (insertError) {
            console.error('❌ Profil oluşturma hatası:', insertError);
            
            // Hata varsa tekrar dene
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            const { error: retryError } = await supabase
              .from('profiles')
              .upsert({ 
                id: data.user.id,
                email: email,
                full_name: fullName,
                phone: phone,
                subscription_status: 'free',
                subscription_plan: null,
                subscription_end_date: null,
                avatar_url: null
              }, {
                onConflict: 'id'
              });
            
            if (retryError) {
              console.error('❌ Profil tekrar deneme hatası:', retryError);
            } else {
              console.log('✅ Profil başarıyla oluşturuldu (tekrar deneme)!');
            }
          } else {
            console.log('✅ Profil başarıyla oluşturuldu!');
          }
        }

        // Son kontrol - profil gerçekten kaydedildi mi?
        const { data: finalProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.user.id)
          .maybeSingle();

        console.log('📋 Son profil durumu:', finalProfile);
      }

      return { error: null };
    } catch (e: any) {
      console.error('Sign up error:', e);
      return { error: e?.message ?? 'An unexpected error occurred.' };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        console.error('Sign in error:', error);
        return { error: error.message };
      }
      return { error: null };
    } catch (e: any) {
      console.error('Sign in error:', e);
      return { error: e?.message ?? 'An unexpected error occurred.' };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.error('Sign out error:', e);
    } finally {
      setProfile(null);
      setUser(null);
      setSession(null);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}${__BASE_PATH__}/auth?type=recovery`,
      });
      if (error) {
        console.error('Reset password error:', error);
        return { error: error.message };
      }
      return { error: null };
    } catch (e: any) {
      console.error('Reset password error:', e);
      return { error: e?.message ?? 'Beklenmeyen bir hata oluştu.' };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        signUp,
        signIn,
        signOut,
        refreshProfile,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

function translateError(msg: string): string {
  if (msg.includes('Invalid login credentials')) return 'E-posta veya şifre hatalı.';
  if (msg.includes('Email not confirmed')) return 'Lütfen e-posta adresinizi doğrulayın.';
  if (msg.includes('User already registered')) return 'Bu e-posta adresi zaten kayıtlı.';
  if (msg.includes('Password should be at least')) return 'Şifre en az 6 karakter olmalıdır.';
  if (msg.includes('Email rate limit exceeded') || msg.includes('rate limit')) return 'Çok fazla deneme. Lütfen biraz bekleyin.';
  return msg;
}
